import {consume} from "@lit/context";
import {t} from "i18next";
import {LitElement, nothing, unsafeCSS, type PropertyValues} from "lit";
import {customElement, state} from "lit/decorators.js";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import {html, unsafeStatic} from "lit/static-html.js";
import GridSmall from "@interstate/icons-svg/grid-small-solid.svg?raw";

import type {SolutionLink, UserLink, Application} from "../../backend";
import {BridgeBarDataContext, modeToTarget} from "../../backend";
import type {BridgeBarDataContextValue} from "../../backend";
import type {BridgeBarConfig} from "../../config";
import {BridgeBarConfigContext} from "../../config";
import {COLLAPSE_COMPONENTS_EVENT, ROOT_TAG} from "../../globals";
import {InterstateIcon, resolveInterstateIcon} from "../../interstate";
import {sanitizeHtml} from "../../security";
import {
  emitApplicationSwitcherToggle,
  emitSolutionGroupToggle,
  emitSolutionNav,
  emitSolutionAppLink
} from "../../analytics";
import styles from "./ApplicationSwitcherElement.css?inline";

export const tagName = `${ROOT_TAG}-application-switcher`;
export const ApplicationSwitcher = unsafeStatic(tagName);

function isMatchingSolutionId(
  solutionId: string | undefined | null,
  applicationId: string | undefined | null
): boolean {
  if (!solutionId || !applicationId) return false;
  return solutionId.toLowerCase().indexOf(applicationId.toLowerCase()) === 0;
}

/**
 * ApplicationSwitcherElement renders the Cox Automotive solution switcher
 * popover. It shows solution_links from the data context and user_links with
 * menu==='solution' as a tail row. When data is unavailable or status is error,
 * it renders nothing (silently hidden per AC-2.3).
 *
 * @fires component-expanded - Dispatched with { detail: { component: 'application-switcher' }, composed: true, bubbles: true } when the popover is opened.
 */
@customElement(tagName)
export class ApplicationSwitcherElement extends LitElement {
  static styles = unsafeCSS(styles);

  @consume({context: BridgeBarDataContext, subscribe: true})
  private dataContext: BridgeBarDataContextValue | undefined;

  @consume({context: BridgeBarConfigContext, subscribe: true})
  private config: BridgeBarConfig | undefined;

  /** Whether the popover is currently open. */
  @state()
  private isOpen = false;

  /** Bound reference to the document click handler for outside-click detection */
  private handleDocumentClick: (e: MouseEvent) => void;

  /** Bound reference to the document keydown handler for Escape key detection */
  private handleDocumentKeyDown: (e: KeyboardEvent) => void;

  /** Bound reference to the container's collapse-all broadcast handler */
  private handleCollapseRequest: () => void;

  /** Set of expanded solution IDs (composed solutions with applications[]). */
  @state()
  private expandedSolutions = new Set<string>();

  constructor() {
    super();
    // Bind handlers once so the same reference can be added and removed
    this.handleDocumentClick = (e: MouseEvent) => {
      if (!e.composedPath().includes(this)) {
        this.closeMenu();
      }
    };
    // Close the menu on Escape regardless of which element has focus
    this.handleDocumentKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && this.isOpen) {
        this.closeMenu();
      }
    };
    // Collapse when the container broadcasts a collapse-all request.
    this.handleCollapseRequest = () => {
      this.closeMenu();
    };
  }
  override connectedCallback(): void {
    super.connectedCallback();
    this.onHashChange = this.onHashChange.bind(this);
    window.addEventListener("hashchange", this.onHashChange);
    document.addEventListener("click", this.handleDocumentClick);
    document.addEventListener("keydown", this.handleDocumentKeyDown);
    document.addEventListener(
      COLLAPSE_COMPONENTS_EVENT,
      this.handleCollapseRequest
    );
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener("hashchange", this.onHashChange);
    document.removeEventListener("click", this.handleDocumentClick);
    document.removeEventListener("keydown", this.handleDocumentKeyDown);
    document.removeEventListener(
      COLLAPSE_COMPONENTS_EVENT,
      this.handleCollapseRequest
    );
  }

  private handleTriggerClick(e: MouseEvent): void {
    if (this.isOpen) {
      emitApplicationSwitcherToggle("collapsed", e, this.config?.getTracker);
      this.closeMenu();
    } else {
      emitApplicationSwitcherToggle("expanded", e, this.config?.getTracker);
      this.openMenu();
    }
  }

  private openMenu() {
    this.isOpen = true;
    this.dispatchEvent(
      new CustomEvent("component-expanded", {
        detail: {component: "application-switcher"},
        composed: true,
        bubbles: true
      })
    );
  }

  private closeMenu() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.dispatchEvent(
      new CustomEvent("component-collapsed", {
        detail: {component: "application-switcher"},
        composed: true,
        bubbles: true
      })
    );
  }

  override willUpdate(_changed: PropertyValues): void {
    // Reflect the configured theme onto the host BEFORE render, so `dark:`
    // Tailwind variants resolve on first paint (prevents light-mode FOUC).
    // Guarded so we don't re-set the attribute on every update.
    // `:host-context()` is not supported in Firefox, so each themed component
    // must carry the attribute on its own host.
    const mode = this.config?.theme ?? "light";
    if (this.getAttribute("data-ids-theme-mode") !== mode) {
      this.setAttribute("data-ids-theme-mode", mode);
    }
  }

  private onHashChange(): void {
    this.closeMenu();
  }

  private get applicationId(): string {
    return this.config?.applicationId ?? "";
  }

  private get solutionLinks(): SolutionLink[] {
    if (this.dataContext?.status === "complete") {
      return this.dataContext.data.solution_links ?? [];
    }
    return [];
  }

  private get userLinks(): UserLink[] {
    if (this.dataContext?.status === "complete") {
      return this.dataContext.data.user_links ?? [];
    }
    return [];
  }

  private get solutionUserLinks(): UserLink[] {
    return this.userLinks.filter(l => l.menu === "solution");
  }

  private get hasSolutionContent(): boolean {
    return this.solutionLinks.length > 0 || this.solutionUserLinks.length > 0;
  }

  /** Returns the SolutionLink whose solutionId matches applicationId, if any.
   * Uses case-insensitive prefix match per legacy solutionSwitcher.js parity. */
  private get currentSolution(): SolutionLink | undefined {
    const appId = this.applicationId;
    if (!appId) return undefined;
    return this.solutionLinks.find(
      s =>
        isMatchingSolutionId(s.solutionId, appId) ||
        s.applications?.some(a => isMatchingSolutionId(a.applicationId, appId))
    );
  }

  private handleSolutionRowClick(solutionId: string, e?: MouseEvent): void {
    const willExpand = !this.expandedSolutions.has(solutionId);
    // Emit before mutating state (AD-8 emit-before-action ordering), using the
    // resulting state. Net-new composed-solution accordion toggle event.
    emitSolutionGroupToggle(
      solutionId,
      willExpand ? "expanded" : "collapsed",
      e,
      this.config?.getTracker
    );
    if (willExpand) {
      this.expandedSolutions = new Set([...this.expandedSolutions, solutionId]);
    } else {
      this.expandedSolutions = new Set(
        [...this.expandedSolutions].filter(id => id !== solutionId)
      );
    }
  }

  private isSolutionSelected(solution: SolutionLink): boolean {
    const appId = this.applicationId;
    if (!appId) return false;
    return (
      isMatchingSolutionId(solution.solutionId, appId) ||
      (solution.applications?.some(a =>
        isMatchingSolutionId(a.applicationId, appId)
      ) ??
        false)
    );
  }

  private isExpandedByDefault(solution: SolutionLink): boolean {
    const appId = this.applicationId;
    if (!appId || !solution.applications?.length) return false;
    return solution.applications.some(a =>
      isMatchingSolutionId(a.applicationId, appId)
    );
  }

  /** Tailwind classes that render the "current-solution" highlight.
   * Light mode: 4px blue left bar + light-blue fill (matches reference).
   * Dark mode:  solid bright-blue fill (no left bar), matches dark reference. */
  private static readonly SELECTED_CLASSES =
    "bg-primary-50 border-0 border-l-4 border-solid border-primary-700 -ml-px " +
    "dark:bg-primary-600 dark:border-transparent dark:text-white";

  private renderApplicationRow(app: Application) {
    const target = modeToTarget(app.mode);
    return html`
      <a
        class="ids-listbox-item pl-8 text-primary-700 hover:text-primary-900 dark:text-white dark:hover:text-white dark:hover:!bg-primary-600 no-underline min-w-0"
        href=${app.uri}
        data-application-id=${app.applicationId}
        target=${target}
        @click=${(e: MouseEvent) =>
          emitSolutionAppLink(
            {applicationId: app.applicationId, label: app.label},
            e,
            this.config?.getTracker
          )}
        @contextmenu=${(e: MouseEvent) =>
          emitSolutionAppLink(
            {applicationId: app.applicationId, label: app.label},
            e,
            this.config?.getTracker
          )}>
        <span class="truncate min-w-0"
          >${unsafeHTML(sanitizeHtml(app.label))}</span
        >
      </a>
    `;
  }

  private renderSolutionRow(solution: SolutionLink) {
    const isSelected = this.isSolutionSelected(solution);
    const hasApplications =
      solution.applications && solution.applications.length > 0;
    const isExpanded =
      this.expandedSolutions.has(solution.solutionId) ||
      this.isExpandedByDefault(solution);
    const target = modeToTarget(solution.mode);
    const selectedClasses = isSelected
      ? ApplicationSwitcherElement.SELECTED_CLASSES
      : "";

    return html`
      ${hasApplications
        ? html`
            <button
              class="ids-listbox-item items-center text-left text-primary-700 hover:text-primary-900 dark:text-white dark:hover:text-white dark:hover:!bg-primary-600 bg-transparent cursor-pointer min-w-0 relative pl-6 ${selectedClasses}"
              aria-selected=${isSelected ? "true" : "false"}
              aria-expanded=${isExpanded ? "true" : "false"}
              data-solution-id=${solution.solutionId}
              @click=${(e: MouseEvent) =>
                this.handleSolutionRowClick(solution.solutionId, e)}>
              <span
                aria-hidden="true"
                class="absolute left-2 top-1/2 w-2 h-2 border-b-2 border-r-2 border-current transition-transform duration-300 ease-out ${isExpanded
                  ? "-translate-y-3/4 rotate-45"
                  : "-translate-y-1/2 -rotate-45"}"></span>
              <span class="truncate min-w-0"
                >${unsafeHTML(sanitizeHtml(solution.label))}</span
              >
            </button>
          `
        : html`
            <a
              class="ids-listbox-item pl-6 text-primary-700 hover:text-primary-900 dark:text-white dark:hover:text-white dark:hover:!bg-primary-600 no-underline min-w-0 ${selectedClasses}"
              aria-selected=${isSelected ? "true" : "false"}
              data-solution-id=${solution.solutionId}
              href=${solution.uri}
              target=${target}
              @click=${(e: MouseEvent) =>
                emitSolutionNav(
                  {
                    type: "group",
                    solutionId: solution.solutionId,
                    label: solution.label
                  },
                  e,
                  this.config?.getTracker
                )}
              @contextmenu=${(e: MouseEvent) =>
                emitSolutionNav(
                  {
                    type: "group",
                    solutionId: solution.solutionId,
                    label: solution.label
                  },
                  e,
                  this.config?.getTracker
                )}>
              <span class="truncate min-w-0"
                >${unsafeHTML(sanitizeHtml(solution.label))}</span
              >
            </a>
          `}
      ${isExpanded && hasApplications
        ? solution.applications.map(app => this.renderApplicationRow(app))
        : nothing}
    `;
  }

  private renderTailRow(link: UserLink) {
    const target = modeToTarget(link.mode);
    // Icon is API-driven via the user_link.icon field, resolved through the
    // shared Interstate icon registry. Unknown names render no icon.
    const iconSvg = resolveInterstateIcon(link.icon);
    return html`
      <a
        class="ids-listbox-item items-center pl-6 text-primary-700 hover:text-primary-900 dark:text-white dark:hover:text-white dark:hover:!bg-primary-600 no-underline min-w-0"
        href=${link.uri}
        target=${target}
        @click=${(e: MouseEvent) =>
          emitSolutionNav(
            {type: "tail", solutionId: "", label: link.label},
            e,
            this.config?.getTracker
          )}
        @contextmenu=${(e: MouseEvent) =>
          emitSolutionNav(
            {type: "tail", solutionId: "", label: link.label},
            e,
            this.config?.getTracker
          )}>
        ${iconSvg
          ? html`<${InterstateIcon}
              svg="${iconSvg}"
              class="flex-shrink-0 w-4 h-4"
            ></${InterstateIcon}>`
          : nothing}
        <span class="truncate min-w-0"
          >${unsafeHTML(sanitizeHtml(link.label))}</span
        >
      </a>
    `;
  }

  private renderTrigger() {
    const current = this.currentSolution;
    return html`
      <button
        data-testid="switcher-trigger"
        class="flex items-center gap-2 bg-transparent border-none cursor-pointer text-primary-700 hover:text-primary-900 dark:text-white dark:hover:text-white p-0"
        aria-expanded=${this.isOpen ? "true" : "false"}
        @click=${(e: MouseEvent) => this.handleTriggerClick(e)}
        @contextmenu=${(e: MouseEvent) => this.handleTriggerClick(e)}>
        <${InterstateIcon} svg="${GridSmall}" class="flex-shrink-0 w-4 h-4"></${InterstateIcon}>
        ${current ? html`<span class="hidden md:block">${unsafeHTML(sanitizeHtml(current.label))}</span>` : nothing}
      </button>
    `;
  }

  private renderPopover() {
    if (!this.isOpen) return nothing;

    const tailLinks = this.solutionUserLinks;

    return html`
      <div
        class="ids-popover ids-listbox min-w-[18rem] max-w-[350px] dark:bg-primary-900 dark:border-primary-700"
        data-testid="popover"
        data-open="true"
        data-ids-variant="compact"
        open
        data-ids-pos="bottom start">
        <div
          data-testid="popover-header"
          role="heading"
          aria-level="2"
          class="font-sans font-semibold text-center text-base text-neutral-600 dark:text-white py-2 px-4">
          ${t("applicationSwitcher.header")}
        </div>
        <div class="ids-listbox-divider"></div>
        <div class="ids-listbox-list">
          ${this.solutionLinks.map(s => this.renderSolutionRow(s))}
        </div>
        ${tailLinks.length > 0
          ? html`
              <div class="ids-listbox-divider"></div>
              <div class="ids-listbox-list" data-testid="solution-tail-list">
                ${tailLinks.map(l => this.renderTailRow(l))}
              </div>
            `
          : nothing}
      </div>
    `;
  }

  override render() {
    // Status guard: render nothing on error or when no solution content
    if (!this.dataContext) {
      return nothing;
    }
    if (this.dataContext.status === "error") {
      return nothing;
    }
    if (this.dataContext.status === "pending") {
      return nothing;
    }
    if (!this.hasSolutionContent) {
      return nothing;
    }

    return html`
      <div class="relative">
        ${this.renderTrigger()} ${this.renderPopover()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [tagName]: ApplicationSwitcherElement;
  }
}
